import Autocomplete from "@/components/Autocomplete";
import AvatarDetails from "@/components/AvatarDetails";
import Button from "@/components/Button";
import DateSelect from "@/components/DateSelect";
import useModal from "@/hooks/useModal";
import { useSearch } from "@/hooks/useSearch";
import { Person } from "@/types";
import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { useForm } from "react-hook-form";
import { CREATE_EVENT_REGISTRATION_MUTATION } from "../../../queries";
import { showSnackbar } from "@/lib/snackbarUtils";
import { useMutation } from "@apollo/client";
import { SOME_PEOPLE_QUERY } from "@/pages/people/queries";
import { capitalize } from "lodash";
import { DateTime } from "luxon";
import { Event } from "../../../types";
import Banner from "@/components/Banner";
import { ApolloError } from "@apollo/client";
import { getDateFromIsoForZone } from "../_utils";
import { useParams } from "react-router-dom";
import useAmplitude from "@/hooks/useAmplitude";
import { EVENT_ANALYTICS_EVENTS, getEventAnalyticsData } from "@/pages/events/_utils/amplitude";

const defaultValues = {
  person: "",
  registrationDate: {
    startDate: DateTime.now().toFormat("yyyy-MM-dd"),
    endDate: DateTime.now().toFormat("yyyy-MM-dd")
  }
};

type FieldValues = typeof defaultValues;

function getDefaultRegistrationDate(
  registrationOpensAt?: string,
  registrationClosesAt?: string,
  timezone?: string
): string {
  const today = DateTime.now();

  if (!registrationOpensAt || !registrationClosesAt || !timezone) {
    return today.toFormat("yyyy-MM-dd");
  }

  const opensAt = DateTime.fromJSDate(getDateFromIsoForZone(registrationOpensAt, timezone));
  const closesAt = DateTime.fromJSDate(getDateFromIsoForZone(registrationClosesAt, timezone));

  if (today < opensAt) return opensAt.toFormat("yyyy-MM-dd");
  if (today <= closesAt) return today.toFormat("yyyy-MM-dd");
  return closesAt.toFormat("yyyy-MM-dd");
}

export default function AddRegistrantModal({
  isAlreadyRegisteredError,
  event,
  onDismiss,
  eventId,
  refetch
}: {
  isAlreadyRegisteredError: (pgaId: string) => boolean;
  event: Pick<Event, "registrationOpenAt" | "registrationCloseAt" | "timezone"> | undefined;
  onDismiss: () => void;
  eventId: string;
  refetch?: () => void;
}) {
  const searchField = "person";
  const [pgaId, setPgaId] = useState("");
  const [bannerError, setBannerError] = useState("");
  const { searchValue, control, reset, setValue } = useSearch(searchField);
  const [variables, setVariables] = useState({});
  const { Modal } = useModal();
  const { data } = useQuery(SOME_PEOPLE_QUERY, { variables });
  const { eventSlug = "" } = useParams();
  const { trackEvent } = useAmplitude();

  const defaultRegistrationDate = getDefaultRegistrationDate(
    event?.registrationOpenAt,
    event?.registrationCloseAt,
    event?.timezone
  );

  const defaultValues = {
    person: "",
    registrationDate: {
      startDate: defaultRegistrationDate,
      endDate: defaultRegistrationDate
    }
  };

  const {
    control: formControl,
    handleSubmit,
    formState: { isValid }
  } = useForm<FieldValues>({
    defaultValues,
    mode: "onChange"
  });

  function selectPayor(name: string, value: Person, setValue: (name: string, value: string) => void) {
    setPgaId(value.pgaId || "");
    setValue(name, `${value.firstName} ${value.lastName}`);
  }

  useEffect(() => {
    setVariables(() => ({
      ...(searchValue ? { input: { search: searchValue } } : {})
    }));
  }, [searchValue]);

  const [createEventRegistration, { loading: mutationLoading }] = useMutation(CREATE_EVENT_REGISTRATION_MUTATION);

  const handleAddRegistrant = handleSubmit(async (fields: FieldValues) => {
    try {
      const variables = {
        input: {
          eventId,
          pgaId,
          registeredAt: DateTime.fromISO(fields.registrationDate.startDate).toISO()
        }
      };

      const { data } = await createEventRegistration({ variables });
      const { success, message } = data.createEventRegistration;
      if (success) {
        refetch && refetch();
        showSnackbar("Registrant added successfully", { close: true });
        onDismiss();
        trackEvent(
          EVENT_ANALYTICS_EVENTS.registrationCreated,
          getEventAnalyticsData({
            source: "registrations",
            eventId,
            eventSlug,
            action: "create"
          })
        );
      } else {
        setBannerError(message);
      }
    } catch (e) {
      setBannerError(`Error: ${(e as ApolloError).message}`);
    }
  });
  const minDate = getDateFromIsoForZone(event?.registrationOpenAt || "", event?.timezone);
  const maxDate = getDateFromIsoForZone(event?.registrationCloseAt || "", event?.timezone);

  return (
    <Modal
      onClosed={onDismiss}
      action={
        <Button onClick={handleAddRegistrant} disabled={mutationLoading || !isValid}>
          Add Registrant
        </Button>
      }
      headline="Add Registrant"
      displayOverflow
    >
      <div className="md:w-[375px] space-y-6">
        {!!bannerError && (
          <div className="mb-6">
            <Banner inline variant="error" description="The registrant could not be added." subText={bannerError} />
          </div>
        )}
        <Controller
          name="person"
          control={control}
          render={({ field: { onChange, value, name } }) => (
            <Autocomplete
              label="Person"
              required
              query={value}
              data={data?.people?.nodes || []}
              onChangeText={onChange}
              reset={reset}
              onSelect={(value) => selectPayor(name, value, setValue)}
              renderItem={(data: Person) => (
                <AvatarDetails
                  title={`${data.firstName} ${data.lastName}`}
                  text={
                    <div className="flex flex-col">
                      <div className="text-sys-brand-on-surface-variant">{data.pgaId}</div>
                      {data.latestProgram && (
                        <div className="text-sys-brand-on-surface-variant">
                          {data.latestProgram?.programType.name!} - {capitalize(data.latestProgram?.status)}
                        </div>
                      )}
                    </div>
                  }
                  picture={data.profilePhoto}
                  secondaryText={data.primarySectionAffiliation?.section?.name || ""}
                />
              )}
              error={!!isAlreadyRegisteredError(pgaId)}
              errorText={isAlreadyRegisteredError(pgaId) ? "This member is already registered to the event" : ""}
            />
          )}
        />
        <Controller
          name="registrationDate"
          control={formControl}
          rules={{
            validate: (value) => {
              if (!value) {
                return `Registration date is required`;
              }
              if (maxDate && minDate) {
                const selectedDate = DateTime.fromISO(value.startDate);
                const maxDateTime = DateTime.fromJSDate(new Date(maxDate));
                const minDateTime = DateTime.fromJSDate(new Date(minDate));

                if (selectedDate < minDateTime || selectedDate > maxDateTime) {
                  return `Registration date must be between ${minDateTime.toFormat(
                    "MM/dd/yyyy"
                  )} and ${maxDateTime.toFormat("MM/dd/yyyy")}`;
                }
              }
              return true;
            }
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <DateSelect
              label="Registration Date"
              value={value}
              useModal={true}
              onChange={(e) => {
                onChange(e);
              }}
              useSingleDate
              showFooter={false}
              error={!!error}
              errorMessage={error?.message}
              minDate={minDate}
              maxDate={maxDate}
            />
          )}
        />
      </div>
    </Modal>
  );
}
